import { AIRO_LOCKED_TEXT, AIRO_QUERY_VERSE, isAiroLicensed } from "../../services/license.service";
import { DatePipe } from "@angular/common";
import { Component, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { forkJoin, Observable, of } from "rxjs";
import { catchError, map, startWith } from "rxjs/operators";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { NgxSpinnerService } from "ngx-spinner";
import { ToastrService } from "ngx-toastr";
import { DeletePopupComponent } from "../delete-popup/delete-popup.component";
import { ActionTrackerService } from "../../services/action-tracker.service";
import textConfiguration from "../../../../assets/static-text-configuration.json";
import { ComplianceService } from "../../services/compliance.service";
import { ActionHistoryComponent } from "../action-history/action-history.component";
import { AiroQueryConfirmDialogComponent } from "../../../pages/incident-tracker/compliance/controls/airo-query-confirm-dialog/airo-query-confirm-dialog.component";
import { CommonService } from "../../services/common.service";
import { environment } from "../../../../environments/environment";

@Component({
  selector: "action-create-modal",
  templateUrl: "action-create-modal.html",
  styleUrls: ["action-create-modal.scss"],
  providers: [DatePipe],
})
export class ActionCreateModalDialogComponent implements OnInit, OnDestroy {
  // Ask Airo needs the tenant's Airo licence to include Query Verse.
  readonly airoLocked = !isAiroLicensed(AIRO_QUERY_VERSE);
  readonly airoLockedText = AIRO_LOCKED_TEXT;
  staticText: any = (textConfiguration as any).common;
  actionForm = new FormGroup({
    id: new FormControl(""),
    title: new FormControl("", [Validators.required]),
    description: new FormControl("", [Validators.required]),
    status: new FormControl("", [Validators.required]),
    assignee_ids: new FormControl("", [Validators.required]),
    action_tracker_type: new FormControl("", [Validators.required]),
    type_id: new FormControl(""),
    due_date: new FormControl("", [Validators.required]),
    assigned_date: new FormControl(""),
    assigned_by: new FormControl(""),
    comments: new FormControl(""),
    completion_date: new FormControl(""),
    linked_action: new FormControl(""),
  });

  teamList: any;
  actionTrackerCategory: any;
  opportunityList: any;
  projectList: any;
  pulsesList: any;
  paradeList: any;
  assessments: any;
  curatedInsightList: any;
  complianceClauseList: any;
  riskList: any;
  metricsList: any;
  categoryOptionsMap: any;
  filteredActionTrackerCategory: any;
  uploadFiles: Array<any> = [];
  uploadedFiles: Array<any> = [];
  assignedBy: string;
  openFrom: string;
  // Plans multi-select support
  plansList: any;
  selectedPlans: any[] = [];
  planUserInputControl = new FormControl();
  plansFilteredOptions: Observable<any[]>;
  // cached label for currently selected `type_id`
  typeLabel: string = "";
  private _typeSub: any;
  // tracks whether the user has typed anything new in the comments field
  private _commentModified = false;
  userDetails: any;
  actionsList: any = [];
  /**
   * Whether the Linked Action field is shown at all. Linking is never done by
   * hand here - other workflows write `linked_action` - so the field only
   * appears on an existing action that already came back linked from the API,
   * and is read-only even then.
   */
  showLinkedAction = false;
  linkedActionDetails: any = null;
  loadingLinkedAction: boolean = false;
  linkedActionCategoryLabel: string = "";
  linkedActionTypeLabel: string = "";
  statusOptionsList = [
    {
      name: "Open",
      display_name: "Open",
    },
    {
      name: "Closed",
      display_name: "Closed",
    },
  ];
  actor: string = "";
  assigneeSearchText: string = "";
  private _lastFetchedLinkedActionId: any = null;
  @ViewChild("actionHistory") actionHistoryComponent!: ActionHistoryComponent;
  loading: boolean;

  get filteredAssignees(): any[] {
    const list = (this.teamList || []).filter(
      (u: any) => u?.first_name || u?.last_name || u?.name
    );
    if (!this.assigneeSearchText) {
      return list;
    }
    const term = this.assigneeSearchText.toLowerCase();
    return list.filter((u: any) => {
      const fullName = `${u?.first_name || ""} ${u?.last_name || ""}`.trim() || u?.name || "";
      const email = u?.email || "";
      return (
        fullName.toLowerCase().includes(term) ||
        email.toLowerCase().includes(term)
      );
    });
  }

  onAssigneeDropdownClose(isOpen: boolean) {
    if (!isOpen) {
      this.assigneeSearchText = "";
    }
  }

  getSelectedAssigneesLabel(): string {
    const selectedIds = this.actionForm?.get("assignee_ids")?.value;
    if (!selectedIds || !selectedIds.length) {
      return "";
    }
    const selectedUsers = (this.teamList || []).filter((u: any) =>
      selectedIds.includes(u.id)
    );
    return selectedUsers
      .map((u: any) => `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.name || u.email)
      .join(", ");
  }

  constructor(
    public readonly dialogRef: MatDialogRef<ActionCreateModalDialogComponent>,
    private readonly actionTracker: ActionTrackerService,
    private readonly complianceService: ComplianceService,
    private readonly commonService: CommonService,

    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly toastrService: ToastrService,
    private readonly spinner: NgxSpinnerService,
    private readonly dialog: MatDialog,
    private readonly datepipe: DatePipe
  ) {}

  ngOnInit() {
    this.userDetails = JSON.parse(localStorage.getItem("permission") ?? "{}");
    this.teamList = this.data.teamList || [];
    this.actionTrackerCategory = this.data.actionTrackerCategory || [];
    this.filteredActionTrackerCategory = (this.actionTrackerCategory || []).filter(
      (cat: any) => {
        return cat.name !== "Others";
      }
    );
    this.opportunityList = this.data.opportunityList || [];
    this.projectList = this.data.projectList || [];
    this.paradeList = this.data.paradeList || [];
    this.pulsesList = this.data.pulseList || [];
    this.curatedInsightList = this.data.curatedInsightList || [];
    this.assessments = this.data.assessments || [];
    this.complianceClauseList = this.data.complianceClausesList || [];
    this.riskList = this.data.riskList || [];
    this.plansList = this.data.plansList || [];
    this.metricsList = this.data.metricsList || [];
    this.categoryOptionsMap = {
      Opportunity: this.opportunityList,
      Project: this.projectList,
      Parade: this.paradeList,
      Pulse: this.pulsesList,
      "Curated Insights": this.curatedInsightList,
      Assessment: this.assessments,
      Compliance: this.complianceClauseList,
      "Compliance Checklist": this.complianceClauseList,
      Risk: this.riskList,
      metrics: this.metricsList,
      // Callers opening this modal for a category the map doesn't hardcode (the
      // balanced scorecard's "metrics", say) supply their own options, keyed by
      // ActionTrackerType.name.
      ...(this.data.categoryOptionsMap || {}),
    };
    this.openFrom = this.data.openFrom || "action_tracker";

    this.loading = true;
    forkJoin({
      actionNames: this.actionTracker
        .getActionNames()
        .pipe(catchError(() => of([]))),
      statusOptions: this.fetchStatusOptions$(),
    }).subscribe(({ actionNames, statusOptions }) => {
      this.actionsList = actionNames || [];
      if (statusOptions?.length) {
        this.statusOptionsList = statusOptions;
      }
      this.initialiseForm();
      this.loading = false;
    });
  }

  private fetchStatusOptions$(): Observable<any> {
    const actor = this.data.actionData?.task?.actor;
    if (!this.data.actionData || !this.isComplianceCategory(this.data.actionData)) {
      return of([]);
    }
    return this.actionTracker
      .getStatusOptions(actor)
      .pipe(catchError(() => of([])));
  }

  /** History is only meaningful for an already-saved action. */
  private fetchHistory$(): Observable<any> {
    const actionId = this.data.actionData?.id;
    if (!actionId) return of({});
    return this.actionTracker
      .getActionHistory(actionId, "action")
      .pipe(catchError(() => of({})));
  }

  private isComplianceCategory(actionData: any): boolean {
    const categoryId = actionData?.action_tracker_type;
    return !!this.actionTrackerCategory?.find(
      (cat: any) =>
        cat.id === categoryId &&
        (cat?.name?.toLowerCase() === "compliance" ||
          cat?.name?.toLowerCase() === "compliance checklist")
    );
  }

  private initialiseForm() {
    this.actionForm.addControl(
      "plans",
      new FormControl(
        this.data.actionData?.plans ? this.data.actionData.plans : []
      )
    );
    this.actionForm
      .get("type_id")
      ?.setValue(this.data.actionData?.type_id || null);
    this.setPlanData();
    if (this.data.actionData) {
      if (this.data.actionData?.assignee) {
        this.data.actionData.assignee_ids = this.data.actionData?.assignee.map(
          (user: any) => user?.id
        );
      }
      this.actor = this.data.actionData?.task?.actor;

      this.uploadedFiles = this.data.actionData?.files ?? [];

      // Patch all fields EXCEPT comments — comments must only be sent if the
      // user explicitly types something new in this session.
      // const { comments: _omitted, ...dataWithoutComments } =
      //   this.data.actionData;
      // this.actionForm.patchValue(dataWithoutComments);
      this.actionForm.patchValue(this.data.actionData);
      // Read off the payload rather than the control: this must reflect what
      // the API sent, not anything the form picks up afterwards.
      this.showLinkedAction =
        !!this.data.actionData?.id && !!this.data.actionData?.linked_action;
      // Default Completion Date to today if status is already Closed and no date is set
      if (
        (this.actionForm.get("status")?.value === "Closed" ||
          this.actor === "reviewer") &&
        !this.actionForm.get("completion_date")?.value
      ) {
        this.actionForm.get("completion_date")?.setValue(new Date());
      }
    }

    // Track whether the user actually modifies the comments field
    this._commentModified = false;
    this.actionForm.get("comments")?.valueChanges.subscribe(() => {
      this._commentModified = true;
    });
    this.actionForm.get("status")?.valueChanges.subscribe((status: string) => {
      if (status === "Closed" || this.actor === "reviewer") {
        if (!this.actionForm.get("completion_date")?.value) {
          this.actionForm.get("completion_date")?.setValue(new Date());
        }
        this.actionForm.get("completion_date")?.markAsDirty();
      }
    });

    const alwaysEnabledKeys = [
      "status",
      "id",
      "comments",
      "due_date",
      "completion_date",
    ];
    // The balanced scorecard prefills the title and description from the metric, but
    // both are only a starting point — the user must still be able to edit them there.
    if (
      this.openFrom === "balanced_scorecard" ||
      this.openFrom === "security_incident"
    ) {
      alwaysEnabledKeys.push("description", "title");
    }

    Object.keys(this.actionForm.controls).forEach((key) => {
      if (!alwaysEnabledKeys.includes(key)) {
        const control = this.actionForm.get(key);
        if (
          control &&
          control?.value !== "" &&
          this.data.actionData?.type_id !== null &&
          this.data.actionData?.type_id !== undefined
        ) {
          control.disable();
        }
      }
    });
    // Always read-only: linking is driven by other workflows, never by the user.
    this.actionForm.get("linked_action")?.disable();

    // compute and cache label once for prefilled value and subscribe to later changes
    this.computeTypeLabel();
    const typeControl = this.actionForm.get("type_id");
    if (typeControl) {
      this._typeSub = typeControl.valueChanges.subscribe(() =>
        this.computeTypeLabel()
      );
    }
  }

  setPlanData() {
    this.selectedPlans = [];
    if (this.data.actionData?.plans) {
      this.data.actionData?.plans.forEach((element: any) => {
        this.plansList.forEach((elm: any) => {
          if (elm.id === element.id) {
            this.selectedPlans.push(elm);
          }
        });
      });
    }
    // setup plans autocomplete
    this.plansFilteredOptions = this.planUserInputControl.valueChanges.pipe(
      startWith(""),
      map((value) => this._filterPlans(value))
    );
  }

  getOptionsForCategory(cat: any) {
    return this.categoryOptionsMap[cat.name] || [];
  }

  onCategorySelection(selectedCategoryId: number) {
    const selectedCat = this.actionTrackerCategory.find(
      (cat: any) => cat.id === selectedCategoryId
    );

    if (!selectedCat) return;

    // Clearing validators for ALL mapped controls
    Object.values(this.categoryOptionsMap).forEach((ctrlName: any) => {
      this.actionForm.get(ctrlName)?.clearValidators();
      this.actionForm.get(ctrlName)?.setValue("");
      this.actionForm.get(ctrlName)?.updateValueAndValidity();
    });

    // validator only to the selected category's control
    const selectedControlName = this.categoryOptionsMap[selectedCat.name];
    if (selectedControlName) {
      this.actionForm
        .get(selectedControlName)
        ?.setValidators(Validators.required);
      this.actionForm.get(selectedControlName)?.updateValueAndValidity();
    }
  }

  onFileSelected(event: any) {
    const files = event.target.files;
    if (files?.length) {
      for (let file of files) {
        const fileAreadyUploaded = this.uploadedFiles.some(
          (uploadedFile: any) => {
            return uploadedFile.file_name === file.name;
          }
        );
        const fileAlreadyInUplaodNew = this.uploadFiles.some(
          (uplaodFile: any) => {
            return uplaodFile.name === file.name;
          }
        );
        if (fileAlreadyInUplaodNew) {
          this.toastrService.error(
            `File ${file.name} is already added to upload`
          );
          break;
        } else if (fileAreadyUploaded) {
          this.toastrService.error(`File ${file.name} already exists`);
          break;
        } else {
          this.commonService.validateFileSize(file).subscribe((isValid) => {
            if (!isValid) {
              this.toastrService.error("Please upload less than 50MB");
              return;
            }
            this.uploadFiles.push(file);
          });
        }
      }
      event.target.value = "";
    }
  }

  private _filterPlans(value: any): any[] {
    if (!this.plansList) return [];
    if (typeof value === "string") {
      const filterValue = value.toLowerCase();
      const removeSelected = (this.plansList || []).filter(
        (el: any) => !this.selectedPlans.includes(el)
      );
      return removeSelected.filter((plan: any) =>
        (plan?.display_name || "").toLowerCase().includes(filterValue)
      );
    }
    return [];
  }

  addPlan(e: any) {
    this.selectedPlans.push(e);
    // Mark plans control as dirty since the array was mutated
    this.actionForm.get("plans")?.markAsDirty();
    this.planUserInputControl.setValue("");
  }

  /** Compute and cache the label for the currently selected `type_id`. */
  computeTypeLabel() {
    const typeId = this.actionForm.get("type_id")?.value;
    if (!typeId) {
      this.typeLabel = "";
      return;
    }

    const catId = this.actionForm.get("action_tracker_type")?.value;
    let options: any[] = [];

    if (catId && this.actionTrackerCategory) {
      const cat = this.actionTrackerCategory.find((c: any) => c.id === catId);
      if (cat) {
        options = this.getOptionsForCategory(cat) || [];
      }
    }

    const opt = (options || []).find((o: any) => o.id === typeId);
    this.typeLabel = opt
      ? opt.opportunity_name ||
        opt.project_name ||
        opt.display_name ||
        opt.name ||
        opt.title ||
        opt.clause_title ||
        opt.description ||
        opt.action_tasks ||
        ""
      : "";
  }

  removePlan(i: number) {
    this.selectedPlans.splice(i, 1);
    // Mark plans control as dirty since the array was mutated
    this.actionForm.get("plans")?.markAsDirty();
  }

  removeFile(fileIndex: number) {
    if (
      fileIndex !== null &&
      fileIndex >= 0 &&
      fileIndex < this.uploadFiles.length
    ) {
      this.uploadFiles.splice(fileIndex, 1);
    }
  }

  removeUploadedFile(fileId: number, fileIndex: number) {
    // Open Confirmations Dialog Box
    let dialog = this.dialog.open(DeletePopupComponent, {
      data: this.uploadedFiles[fileIndex].file_name,
    });

    dialog.afterClosed().subscribe((res: any) => {
      if (res) {
        // Call Remove Files API
        this.actionTracker.deleteFile(fileId).subscribe(
          (res: any) => {
            this.uploadedFiles.splice(fileIndex, 1);
            this.toastrService.success("File removed");
            if (this.actionHistoryComponent) {
              this.actionHistoryComponent.loadActivity();
            }
          },
          (err: any) => {
            this.toastrService.error("Failed to remove file!");
          }
        );
      }
    });
  }

  downloadFile(fileId: number, fileIndex: number) {
    this.actionTracker.downloadActionTrackerFile(fileId).subscribe(
      (res: any) => {
        const downloadLink = document.createElement("a");
        downloadLink.href = window.URL.createObjectURL(res);
        downloadLink.setAttribute(
          "download",
          this.uploadedFiles[fileIndex].file_name
        );
        document.body.appendChild(downloadLink);
        downloadLink.click();
        this.toastrService.success("File downloaded successfully");
      },
      (err: any) => {
        this.toastrService.error("Unable to download file");
      }
    );
  }

  downloadLinkedActionFile(fileId: number, fileName: string): void {
    this.actionTracker.downloadActionTrackerFile(fileId).subscribe(
      (res: any) => {
        const downloadLink = document.createElement("a");
        downloadLink.href = window.URL.createObjectURL(res);
        downloadLink.setAttribute("download", fileName);
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.remove();
        this.toastrService.success("File downloaded successfully");
      },
      (err: any) => {
        this.toastrService.error("Unable to download file");
      }
    );
  }

  async createAction() {
    this.actionForm.markAllAsTouched();
    if (this.actionForm.invalid) return;

    const formValues = this.actionForm.getRawValue();

    // Handle Date Transformation
    if (formValues.due_date) {
      formValues.due_date = this.datepipe.transform(
        formValues.due_date,
        "yyyy-MM-dd"
      );
      // Mark as dirty since we transformed it (for update scenarios)
      this.actionForm.get("due_date")?.markAsDirty();
    }

    if (formValues.completion_date) {
      formValues.completion_date = this.datepipe.transform(
        formValues.completion_date,
        "yyyy-MM-dd"
      );
      // Mark as dirty since we transformed it (for update scenarios)
      this.actionForm.get("completion_date")?.markAsDirty();
    }

    // Prepare FormData for create
    const formData = this.prepareFormData(formValues, false);

    // Submit Data
    this.submitData(formData, formValues.id);
  }

  updateAction() {
    this.actionForm.markAllAsTouched();
    if (this.actionForm.invalid) return;

    const formValues = this.actionForm.getRawValue();

    // Handle Date Transformation
    if (formValues.due_date) {
      formValues.due_date = this.datepipe.transform(
        formValues.due_date,
        "yyyy-MM-dd"
      );
      // Mark as dirty since we transformed it (for update scenarios)
      this.actionForm.get("due_date")?.markAsDirty();
    }

    if (formValues.completion_date) {
      formValues.completion_date = this.datepipe.transform(
        formValues.completion_date,
        "yyyy-MM-dd"
      );
      // Mark as dirty since we transformed it (for update scenarios)
      this.actionForm.get("completion_date")?.markAsDirty();
    }

    // Prepare FormData for update
    const formData = this.prepareFormData(formValues, true);

    // Submit Data
    this.submitData(formData, formValues.id);
  }

  private prepareFormData(formValues: any, isUpdate: boolean): FormData {
    const formData = new FormData();

    Object.keys(this.actionForm.controls).forEach((key) => {
      const control = this.actionForm.get(key);
      const value = formValues[key];

      // Skip null/undefined
      if (value === null || value === undefined) return;

      // For UPDATE → skip non-id fields that aren't dirty
      if (
        isUpdate &&
        key !== "id" &&
        key !== "action_tracker_type" &&
        key !== "type_id" &&
        !control?.dirty
      )
        return;

      // Only include comments if the user explicitly modified the field
      if (key === "comments" && !this._commentModified) return;

      // Plans handling
      if (key === "plans" && Array.isArray(value)) {
        this.selectedPlans.forEach((plan: any) =>
          formData.append("plan_ids", plan?.id)
        );
      }
      // Generic array handling
      else if (Array.isArray(value)) {
        value.forEach((item) => formData.append(key, item));
      }
      // Default case
      else {
        formData.append(key, value);
      }
    });

    const extraFormData = this.data.extraFormData || {};
    Object.keys(extraFormData).forEach((key) => {
      const value = extraFormData[key];
      if (value === null || value === undefined) return;
      formData.append(key, value);
    });

    if (this.uploadFiles?.length) {
      this.uploadFiles.forEach((file) => formData.append("files", file));
    }

    return formData;
  }

  private submitData(formData: FormData, id?: string) {
    this.spinner.show();
    this.loading = true;
    const request$ = id
      ? this.actionTracker.updateActionTracerById(formData, id)
      : this.actionTracker.addNewActionTracker(formData);

    request$.subscribe({
      next: (res: any) => {
        this.toastrService.success(
          `Action ${id ? "updated" : "created"} successfully`
        );
        // Close with the saved action so callers can pick up its id (the scorecard
        // needs it to swap "Create action" for "View action" without a refetch).
        this.dialogRef.close(res ?? true);
      },
      error: () => {
        this.toastrService.error("An error occurred during submission");
        this.loading = false;
      },
      complete: () => {
        this.spinner.hide();
        this.loading = false;
      }
    });
  }

  selectedOptionLabel() {
    // return cached label;
    return this.typeLabel;
  }

  ngOnDestroy(): void {
    if (this._typeSub && this._typeSub.unsubscribe) {
      this._typeSub.unsubscribe();
    }
  }

  closeModal(bool = false): void {
    this.dialogRef.close(bool);
  }

  showAiroButton() {
    const selectedCatId = this.actionForm.get("action_tracker_type")?.value;

    const isComplianceCategory = !!this.actionTrackerCategory?.find(
      (cat: any) =>
        cat.id === selectedCatId &&
        ["compliance", "compliance checklist"].includes(
          cat?.name?.toLowerCase()
        )
    );

    if (!isComplianceCategory) {
      return false;
    }

    const task = this.data.actionData?.task;
    return task?.evidence_type === "databacked";
  }

  openAiroDialog() {
    if (this.airoLocked) return;
    const actionData = this.data.actionData;
    const task = actionData?.task;
    const obligationId = this.actionForm.get("type_id")?.value;

    if (!obligationId) {
      this.toastrService.warning("Please select a compliance clause first.");
      return;
    }

    if (!task || !task.control_id) {
      this.toastrService.error("No control linked to this task.");
      return;
    }

    this.spinner.show();
    // Use forkJoin to fetch both obligation and control details
    import("rxjs").then(({ forkJoin }) => {
      forkJoin({
        obligation: this.complianceService.getComplianceClauseById(
          Number(obligationId)
        ),
        control: this.complianceService.getObligationControls(task.control_id),
      }).subscribe({
        next: ({ obligation, control }) => {
          this.spinner.hide();

          let query = control.natural_lang_query || "";

          const replacements: any = {
            "{{control_name}}": control?.name || "",
            "{{control_description}}": control?.description || "",
            "{{control_id}}": control?.id || "",
            "{{obligation_id}}": obligationId || "",
            "{{task_id}}": task?.task_id || "",
            "{{current_date}}": new Date().toISOString().split("T")[0],
            "{{current_timestamp}}": new Date().toISOString(),
            "{{obligation_summary}}": obligation?.clause_title || "",
            "{{obligation_due_date}}": obligation?.due_date || "",
            "{{obligation_penalty}}": obligation?.penalty_text || "",
          };

          for (const key of Object.keys(replacements)) {
            query = query.split(key).join(replacements[key]);
          }

          const dialogRef = this.dialog.open(AiroQueryConfirmDialogComponent, {
            width: "500px",
            autoFocus: false,
            disableClose: true,
            data: { query: query },
          });

          dialogRef.afterClosed().subscribe((finalQuery: any) => {
            if (finalQuery) {
              window.open(
                `${environment.airoFrontendURL}/verse/structured/home?project_name=${control.repo}&query=${encodeURIComponent(finalQuery)}`,
                "_blank"
              );
            }
          });
        },
        error: () => {
          this.spinner.hide();
          this.toastrService.error("Failed to load details for Airo query");
        },
      });
    });
  }

  isComplianceAction(): boolean {
    const selectedCatId = this.actionForm.get("action_tracker_type")?.value;
    const complianceCat = this.actionTrackerCategory?.find(
      (cat: any) =>
        cat.id === selectedCatId &&
        (cat?.name?.toLowerCase() === "compliance" ||
          cat?.name?.toLowerCase() === "compliance checklist")
    );
    return complianceCat;
  }

  onLinkedActionAccordionOpened(): void {
    const linkedActionId = this.actionForm.get("linked_action")?.value;
    if (!linkedActionId) return;

    // Avoid refetching if we already have details for this exact linked action
    if (
      this.linkedActionDetails &&
      this._lastFetchedLinkedActionId === linkedActionId
    ) {
      return;
    }

    this.loadingLinkedAction = true;
    this.linkedActionDetails = null;
    this.linkedActionCategoryLabel = "";
    this.linkedActionTypeLabel = "";

    this.actionTracker.getActionTrackerDetails(linkedActionId).subscribe({
      next: (resp: any) => {
        this.linkedActionDetails = resp;
        this._lastFetchedLinkedActionId = linkedActionId;
        this.computeLinkedActionCategoryAndType(resp);
        this.loadingLinkedAction = false;
      },
      error: () => {
        this.toastrService.error("Failed to load linked action details");
        this.loadingLinkedAction = false;
      },
    });
  }

  private computeLinkedActionCategoryAndType(linkedAction: any): void {
    const catId = linkedAction?.action_tracker_type;
    const typeId = linkedAction?.type_id;

    this.linkedActionCategoryLabel = "";
    this.linkedActionTypeLabel = "";

    if (!catId || !this.actionTrackerCategory) return;

    const cat = this.actionTrackerCategory.find((c: any) => c.id === catId);
    if (!cat) return;

    this.linkedActionCategoryLabel = cat.display_name || "";

    if (!typeId) return;

    const options = this.getOptionsForCategory(cat) || [];
    const opt = options.find((o: any) => o.id === typeId);

    this.linkedActionTypeLabel = opt
      ? opt.opportunity_name ||
        opt.project_name ||
        opt.name ||
        opt.title ||
        opt.clause_title ||
        opt.description ||
        opt.action_tasks ||
        ""
      : "";
  }
}
